import { useLazyListGroupsQuery } from './groupApi'
import { useLazyListPartiesQuery, useLazyListUsersQuery } from './partyApi'
import { useLazyListFeaturesQuery } from './featureApi'
import { useLazyListDataZonesQuery } from './datazoneApi'
import { useLazyListEntitiesQuery } from './entityApi'
import { useLazyListContractsQuery } from './contractApi'
import { useLazyListIndustriesQuery } from './industryApi'
import { useLazyListServicesQuery } from './serviceApi'
import { useLazyListAssetsQuery } from './assetApi'
import { useLazyListSkillsetsQuery } from './skillsetApi'

const useSearchCallback = () => {
  const [loadGroups] = useLazyListGroupsQuery()
  const [loadParties] = useLazyListPartiesQuery()
  const [loadUsers] = useLazyListUsersQuery()
  const [loadFeatures] = useLazyListFeaturesQuery()
  const [loadDataZones] = useLazyListDataZonesQuery()
  const [loadEntities] = useLazyListEntitiesQuery()
  const [loadContracts] = useLazyListContractsQuery()
  const [loadIndustries] = useLazyListIndustriesQuery()
  const [loadServices] = useLazyListServicesQuery()
  const [loadAssets] = useLazyListAssetsQuery()
  const [loadSkillsets] = useLazyListSkillsetsQuery()

  /// no country no states ==> search for countries
  /// only countries, no states ==> search for states
  /// no countries, only states ==> search for cities (state object contains country code to narrow down the search)
  /// everything else, throw error to wake up the caller
  const searchCountryStateCityCallback = async (
    { countries, states },
    keyword
  ) => {
    const { Country, State, City } = await import('country-state-city')

    const searchCountries = keyword => {
      if (!keyword) keyword = ''
      const countryList = Country.getAllCountries().filter(c =>
        c.name.toLowerCase().includes(keyword.toLowerCase())
      )

      return countryList
    }
    const searchStates = (countryCodeArray, keyword) => {
      if (!keyword) keyword = ''
      const stateList = []
      countryCodeArray.forEach(c => {
        State.getStatesOfCountry(c)
          .filter(s => s.name.toLowerCase().includes(keyword.toLowerCase()))
          .forEach(s => {
            stateList.push(s)
          })
      })
      return stateList
    }
    const searchCities = (stateArray, keyword) => {
      if (!keyword) keyword = ''

      const cityList = []
      stateArray.forEach(s => {
        City.getCitiesOfState(s.countryCode, s.isoCode)
          .filter(c => c.name.toLowerCase().includes(keyword.toLowerCase()))
          .forEach(c => {
            cityList.push(c)
          })
      })
      return cityList
    }
    if (
      (!countries || countries.length === 0) &&
      (!states || states.length === 0)
    )
      return searchCountries(keyword)

    if (countries && countries.length && (!states || states.length === 0))
      return searchStates(
        countries.map(o => o.isoCode),
        keyword
      )

    if ((!countries || countries.length === 0) && states && states.length)
      return searchCities(states, keyword)

    throw new Error('incorrect search parameter configuration')
  }

  const searchIndustriesCallback = async keyword => {
    const {
      data: { data } = {
        data: []
      }
    } = await loadIndustries()
    const remapped = data
      .filter(o => o.name.includes(keyword))
      .map(o => {
        const n = { ...o }
        return n
      })

    return remapped
  }

  const searchEntitiesCallback = async (keyword, subType = null) => {
    const shallowPartyFetchParameter = {
      sort: '+name',
      page: 0,
      per_page: 100,
      keywords: keyword,
      type: subType
    }
    const {
      data: { data } = {
        data: []
      }
    } = await loadEntities(shallowPartyFetchParameter)
    const remapped = data.map(o => {
      const n = { ...o }
      return n
    })

    return remapped
  }

  const searchContractsCallback = async (keyword, subType = null) => {
    const shallowPartyFetchParameter = {
      sort: '+name',
      page: 0,
      per_page: 100,
      keywords: keyword,
      type: subType
    }
    const {
      data: { data } = {
        data: []
      }
    } = await loadContracts(shallowPartyFetchParameter)
    const remapped = data.map(o => {
      const n = { ...o }
      return n
    })

    return remapped
  }

  const searchFeaturesCallback = async (keyword, subType = null) => {
    const shallowPartyFetchParameter = {
      sort: '+name',
      page: 0,
      per_page: 100,
      keywords: keyword,
      type: subType
    }
    const {
      data: { data } = {
        data: []
      }
    } = await loadFeatures(shallowPartyFetchParameter)
    const remapped = data.map(o => {
      const n = { ...o }
      return n
    })
    console.log('fresh loaded features')

    console.log(remapped)
    return remapped
  }

  const searchOnlyUsersCallback = async (keywords) => {
    return await searchPartiesCallback(keywords, 'user')
  }

  const searchOnlyContactsCallback = async (keywords) => {
    return await searchPartiesCallback(keywords, 'contact')
  }

  const searchPartiesCallback = async (keyword, subType = null) => {
    const shallowPartyFetchParameter = {
      sort: '+last_name',
      page: 0,
      per_page: 100
    }

    if (keyword) {
      shallowPartyFetchParameter.keywords = keyword
    }

    let data = []
    if (subType === 'user') {
      data = (await loadUsers(shallowPartyFetchParameter))?.data?.data || []
    } else {
      data = (await loadParties(shallowPartyFetchParameter))?.data?.data || []
    }

    const remapped = data.map(o => {
      const n = { ...o }
      n.name = o.first_name + ' ' + o.last_name
      return n
    })

    return remapped
  }

  const searchSkillsetsCallback = async keyword => {
    const shallowPartyFetchParameter = {
      sort: '+name',
      page: 0,
      per_page: 100,
      keywords: keyword
    }
    const {
      data: { data } = {
        data: []
      }
    } = await loadSkillsets(shallowPartyFetchParameter)
    const remapped = data.map(o => {
      const n = { ...o }
      return n
    })

    return remapped
  }

  const searchDatazonesCallback = async (keyword, subType = null) => {
    const shallowPartyFetchParameter = {
      sort: '+name',
      page: 0,
      per_page: 100,
      keywords: keyword,
      type: subType
    }
    const {
      data: { data } = {
        data: []
      }
    } = await loadDataZones(shallowPartyFetchParameter)
    const remapped = data.map(o => {
      const n = { ...o }
      return n
    })

    return remapped
  }

  const searchGroupsCallback = async (keyword, subType = null, noSystem = false) => {
    const shallowGroupFetchParameter = {
      sort: '+name',
      page: 0,
      per_page: 100,
      keywords: keyword,
      type: subType
    }
    const {
      data: { data } = {
        data: []
      }
    } = await loadGroups(shallowGroupFetchParameter)

    //This is not very ideal, the code for loading the dropdowns is pretty ridged and this is the best place I could find 
    //to purge the current group out of the inheritId selctor to fix the db crash issue when assigning a group to inherit from itself
    //In future would like to rework bucketConfig to allow for passing an ID to exclude.
    const page = window.location.href.split('/')[3]
    var purgedData = []
    if (page === 'groupDetail')
    {
      const currentPageId = window.location.href.split('/')[4]
      for (var i = 0; i < data.length; i++)
      {
        if (data[i].id !== currentPageId)
        {
          purgedData.push(data[i])
        }
      }
    }else { purgedData = data }

    const remapped = purgedData.map(o => {
      const n = { ...o }
      return n
    })

    return noSystem
      ? remapped.filter(g => !g.is_system)
      : remapped
  }

  const searchAssetsCallback = async (keyword, subType = null) => {
    const shallowGroupFetchParameter = {
      sort: '+name',
      page: 0,
      per_page: 100,
      keywords: keyword,
      type: subType
    }
    const {
      data: { data } = {
        data: []
      }
    } = await loadAssets(shallowGroupFetchParameter)

    const remapped = data.map(o => {
      const n = { ...o }
      return n
    })

    return remapped
  }

  const searchNonContainerAssetsCallback = async (keyword, subType = null, typeId = null) => {
    const shallowGroupFetchParameter = {
      sort: '+name',
      page: 0,
      per_page: 100,
      keywords: keyword,
      exclude_containers: true,
      ...(typeId && { type_id: typeId }),
      type: subType
    }
    const {
      data: { data } = {
        data: []
      }
    } = await loadAssets(shallowGroupFetchParameter)

    const remapped = data.map(o => {
      const n = { ...o }
      return n
    })

    return remapped
  }

  const searchServicesCallback = async (keyword, subType = null) => {
    const shallowGroupFetchParameter = {
      sort: '+name',
      page: 0,
      per_page: 100,
      keywords: keyword,
      type: subType
    }
    const {
      data: { data } = {
        data: []
      }
    } = await loadServices(shallowGroupFetchParameter)

    const remapped = data.map(o => {
      const n = { ...o }
      return n
    })

    return remapped
  }

  return {
    searchDatazonesCallback,
    searchGroupsCallback,
    searchPartiesCallback,
    searchOnlyUsersCallback,
    searchOnlyContactsCallback,
    searchFeaturesCallback,
    searchContractsCallback,
    searchEntitiesCallback,
    searchIndustriesCallback,
    searchCountryStateCityCallback,
    searchAssetsCallback,
    searchServicesCallback,
    searchSkillsetsCallback,
    searchNonContainerAssetsCallback
  }
}

export default useSearchCallback
